import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import storeService from '../../services/storeService';
import { useConstants } from '../../context/ConstantsContext';
import { FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

// Reusable Form for Payment Methods
const PaymentMethodForm = ({ method, onSave, onCancel }) => {
  const [formData, setFormData] = useState({ name: '', surcharge: 0, enabled: true });
  const { constants } = useConstants();

  useEffect(() => {
    if (method) {
      setFormData(method);
    } else {
      setFormData({ name: constants?.PAYMENT_METHODS?.CASH || 'Cash', surcharge: 0, enabled: true });
    }
  }, [method, constants]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };
  const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <select name="name" value={formData.name} onChange={handleChange} className="p-2 border rounded-md w-full">
        {constants && Object.values(constants.PAYMENT_METHODS).map(pm => <option key={pm} value={pm}>{pm}</option>)}
      </select>
      <input name="surcharge" type="number" value={formData.surcharge} onChange={handleChange} placeholder="Surcharge (%)" className="p-2 border rounded-md w-full" />
      <label className="flex items-center"><input type="checkbox" name="enabled" checked={formData.enabled} onChange={handleChange} className="mr-2" /> Enabled</label>
      <div className="flex justify-end space-x-3 pt-4"><button type="button" onClick={onCancel} className="bg-gray-200 px-4 py-2 rounded-md">Cancel</button><button type="submit" className="bg-primary text-white px-4 py-2 rounded-md">Save</button></div>
    </form>
  );
};

const PaymentSettings = () => {
  const [methods, setMethods] = useState([]);
  const [storeInfo, setStoreInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [toggleSaving, setToggleSaving] = useState(false);
  const [toggleError, setToggleError] = useState('');
  const { user, updateUser } = useAuth();
  const { id: storeId } = useParams();

  const fetchData = useCallback(async () => {
    const data = await storeService.getStoreById(storeId);
    setMethods(data.paymentMethods || []);
    setStoreInfo(data);
    setIsLoading(false);
  }, [storeId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleOpenModal = (method = null) => { setEditingMethod(method); setIsModalOpen(true); };
  const handleCloseModal = () => { setEditingMethod(null); setIsModalOpen(false); };

  const handleSave = async (formData) => {
    if (editingMethod) {
      await storeService.updatePaymentMethod(storeId, editingMethod._id, formData);
    } else {
      await storeService.addPaymentMethod(storeId, formData);
    }
    fetchData();
    handleCloseModal();
  };

  const handleDelete = async (methodId) => {
    if (window.confirm('Are you sure?')) {
      await storeService.deletePaymentMethod(storeId, methodId);
      fetchData();
    }
  };

  const handleDenominationToggle = async (enabled) => {
    setToggleSaving(true);
    setToggleError('');
    try {
      const updated = await storeService.updateStore(storeId, { cashDenominationPadEnabled: enabled });
      setStoreInfo(prev => ({ ...(prev || {}), cashDenominationPadEnabled: updated.cashDenominationPadEnabled }));
      if (user) {
        const updatedStores = (user.stores || []).map((store) =>
          store._id === updated._id ? { ...store, cashDenominationPadEnabled: updated.cashDenominationPadEnabled } : store
        );
        updateUser({ ...user, stores: updatedStores });
      }
    } catch (err) {
      setToggleError(err.response?.data?.message || 'Unable to update cash denomination setting.');
    } finally {
      setToggleSaving(false);
    }
  };

  const handleTillOpeningToggle = async (enabled) => {
    setToggleSaving(true);
    setToggleError('');
    try {
      const updated = await storeService.updateStore(storeId, { tillOpeningDirectEntryEnabled: enabled });
      setStoreInfo((prev) => ({
        ...(prev || {}),
        tillOpeningDirectEntryEnabled: updated.tillOpeningDirectEntryEnabled,
      }));
      if (user) {
        const updatedStores = (user.stores || []).map((store) =>
          store._id === updated._id
            ? { ...store, tillOpeningDirectEntryEnabled: updated.tillOpeningDirectEntryEnabled }
            : store
        );
        updateUser({ ...user, stores: updatedStores });
      }
    } catch (err) {
      setToggleError(err.response?.data?.message || 'Unable to update till opening options.');
    } finally {
      setToggleSaving(false);
    }
  };

  const handleSplitPaymentsToggle = async (enabled) => {
    setToggleSaving(true);
    setToggleError('');
    try {
      const updated = await storeService.updateStore(storeId, { splitPaymentsEnabled: enabled });
      setStoreInfo((prev) => ({
        ...(prev || {}),
        splitPaymentsEnabled: updated.splitPaymentsEnabled,
      }));
      if (user) {
        const updatedStores = (user.stores || []).map((store) =>
          store._id === updated._id
            ? { ...store, splitPaymentsEnabled: updated.splitPaymentsEnabled }
            : store
        );
        updateUser({ ...user, stores: updatedStores });
      }
    } catch (err) {
      setToggleError(err.response?.data?.message || 'Unable to update split payment setting.');
    } finally {
      setToggleSaving(false);
    }
  };

  const handleCashCardQuickToggle = async (enabled) => {
    setToggleSaving(true);
    setToggleError('');
    try {
      const updated = await storeService.updateStore(storeId, { cashCardQuickPaymentEnabled: enabled });
      setStoreInfo((prev) => ({
        ...(prev || {}),
        cashCardQuickPaymentEnabled: updated.cashCardQuickPaymentEnabled,
      }));
      if (user) {
        const updatedStores = (user.stores || []).map((store) =>
          store._id === updated._id
            ? { ...store, cashCardQuickPaymentEnabled: updated.cashCardQuickPaymentEnabled }
            : store
        );
        updateUser({ ...user, stores: updatedStores });
      }
    } catch (err) {
      setToggleError(err.response?.data?.message || 'Unable to update Cash & Card quick payment setting.');
    } finally {
      setToggleSaving(false);
    }
  };

  const handleSplitRefundCashOverrideToggle = async (enabled) => {
    setToggleSaving(true);
    setToggleError('');
    try {
      const updated = await storeService.updateStore(storeId, { splitRefundCashOverrideEnabled: enabled });
      setStoreInfo((prev) => ({
        ...(prev || {}),
        splitRefundCashOverrideEnabled: updated.splitRefundCashOverrideEnabled,
      }));
      if (user) {
        const updatedStores = (user.stores || []).map((store) =>
          store._id === updated._id
            ? { ...store, splitRefundCashOverrideEnabled: updated.splitRefundCashOverrideEnabled }
            : store
        );
        updateUser({ ...user, stores: updatedStores });
      }
    } catch (err) {
      setToggleError(err.response?.data?.message || 'Unable to update split refund cash override setting.');
    } finally {
      setToggleSaving(false);
    }
  };

  const handleCheckoutB2BOrderToggle = async (enabled) => {
    setToggleSaving(true);
    setToggleError('');
    try {
      const updated = await storeService.updateStore(storeId, { checkoutB2BOrderEnabled: enabled });
      setStoreInfo((prev) => ({
        ...(prev || {}),
        checkoutB2BOrderEnabled: updated.checkoutB2BOrderEnabled,
      }));
      if (user) {
        const updatedStores = (user.stores || []).map((store) =>
          store._id === updated._id
            ? { ...store, checkoutB2BOrderEnabled: updated.checkoutB2BOrderEnabled }
            : store
        );
        updateUser({ ...user, stores: updatedStores });
      }
    } catch (err) {
      setToggleError(err.response?.data?.message || 'Unable to update checkout B2B order setting.');
    } finally {
      setToggleSaving(false);
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <div>
      <div className="mb-8 border border-slate-200 rounded-lg p-4 bg-white shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Cash denomination pad</h3>
            <p className="text-sm text-slate-500">
              Show Australian note and coin shortcuts on the BoltinPOS checkout when taking cash. Staff can tap images to
              fill the "cash tendered" amount quickly.
            </p>
            {toggleError && <p className="text-xs text-red-600 mt-2">{toggleError}</p>}
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only"
              checked={Boolean(storeInfo?.cashDenominationPadEnabled)}
              onChange={(e) => handleDenominationToggle(e.target.checked)}
              disabled={toggleSaving}
            />
            <span
              className={`w-12 h-6 flex items-center bg-gray-300 rounded-full p-1 transition ${
                storeInfo?.cashDenominationPadEnabled ? 'bg-primary' : ''
              } ${toggleSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`bg-white h-4 w-4 rounded-full shadow transform transition ${
                  storeInfo?.cashDenominationPadEnabled ? 'translate-x-6' : ''
                }`}
              />
            </span>
            <span className="ml-3 text-sm font-medium text-slate-700">
              {storeInfo?.cashDenominationPadEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          This feature is only shown on BoltinPOS (desktop/tablet) and has no effect on the BoltinMPOS handheld app.
        </p>
      </div>
      <div className="mb-8 border border-slate-200 rounded-lg p-4 bg-white shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Split payments</h3>
            <p className="text-sm text-slate-500">
              Allow one sale to be paid using multiple tenders (cash + card, card + card).
            </p>
            {toggleError && <p className="text-xs text-red-600 mt-2">{toggleError}</p>}
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only"
              checked={Boolean(storeInfo?.splitPaymentsEnabled)}
              onChange={(e) => handleSplitPaymentsToggle(e.target.checked)}
              disabled={toggleSaving}
            />
            <span
              className={`w-12 h-6 flex items-center bg-gray-300 rounded-full p-1 transition ${
                storeInfo?.splitPaymentsEnabled ? 'bg-primary' : ''
              } ${toggleSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`bg-white h-4 w-4 rounded-full shadow transform transition ${
                  storeInfo?.splitPaymentsEnabled ? 'translate-x-6' : ''
                }`}
              />
            </span>
            <span className="ml-3 text-sm font-medium text-slate-700">
              {storeInfo?.splitPaymentsEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          When disabled, checkout uses the existing single-tender flow only.
        </p>
      </div>
      <div className="mb-8 border border-slate-200 rounded-lg p-4 bg-white shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Cash &amp; Card quick payment</h3>
            <p className="text-sm text-slate-500">
              Show a faster two-step option on checkout: one cash portion first, then one card payment for the remaining amount.
            </p>
            {toggleError && <p className="text-xs text-red-600 mt-2">{toggleError}</p>}
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only"
              checked={Boolean(storeInfo?.cashCardQuickPaymentEnabled)}
              onChange={(e) => handleCashCardQuickToggle(e.target.checked)}
              disabled={toggleSaving}
            />
            <span
              className={`w-12 h-6 flex items-center bg-gray-300 rounded-full p-1 transition ${
                storeInfo?.cashCardQuickPaymentEnabled ? 'bg-primary' : ''
              } ${toggleSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`bg-white h-4 w-4 rounded-full shadow transform transition ${
                  storeInfo?.cashCardQuickPaymentEnabled ? 'translate-x-6' : ''
                }`}
              />
            </span>
            <span className="ml-3 text-sm font-medium text-slate-700">
              {storeInfo?.cashCardQuickPaymentEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          This mode only supports one cash leg and one card leg for faster cashier workflow.
        </p>
      </div>
      <div className="mb-8 border border-slate-200 rounded-lg p-4 bg-white shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Split refund as cash</h3>
            <p className="text-sm text-slate-500">
              Allow split-origin sales to be refunded fully as cash, without processing separate card refund legs.
            </p>
            {toggleError && <p className="text-xs text-red-600 mt-2">{toggleError}</p>}
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only"
              checked={Boolean(storeInfo?.splitRefundCashOverrideEnabled)}
              onChange={(e) => handleSplitRefundCashOverrideToggle(e.target.checked)}
              disabled={toggleSaving}
            />
            <span
              className={`w-12 h-6 flex items-center bg-gray-300 rounded-full p-1 transition ${
                storeInfo?.splitRefundCashOverrideEnabled ? 'bg-primary' : ''
              } ${toggleSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`bg-white h-4 w-4 rounded-full shadow transform transition ${
                  storeInfo?.splitRefundCashOverrideEnabled ? 'translate-x-6' : ''
                }`}
              />
            </span>
            <span className="ml-3 text-sm font-medium text-slate-700">
              {storeInfo?.splitRefundCashOverrideEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          When enabled, the Returns screen offers a "refund full amount as cash" option for split payments.
        </p>
      </div>
      <div className="mb-8 border border-slate-200 rounded-lg p-4 bg-white shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Till opening options</h3>
            <p className="text-sm text-slate-500">
              Allow staff to open a till by entering the total float directly or by counting denominations.
            </p>
            {toggleError && <p className="text-xs text-red-600 mt-2">{toggleError}</p>}
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only"
              checked={Boolean(storeInfo?.tillOpeningDirectEntryEnabled)}
              onChange={(e) => handleTillOpeningToggle(e.target.checked)}
              disabled={toggleSaving}
            />
            <span
              className={`w-12 h-6 flex items-center bg-gray-300 rounded-full p-1 transition ${
                storeInfo?.tillOpeningDirectEntryEnabled ? 'bg-primary' : ''
              } ${toggleSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`bg-white h-4 w-4 rounded-full shadow transform transition ${
                  storeInfo?.tillOpeningDirectEntryEnabled ? 'translate-x-6' : ''
                }`}
              />
            </span>
            <span className="ml-3 text-sm font-medium text-slate-700">
              {storeInfo?.tillOpeningDirectEntryEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          When enabled, the Open Till modal shows a quick total entry option alongside denominations.
        </p>
      </div>
      <div className="mb-8 border border-slate-200 rounded-lg p-4 bg-white shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Checkout B2B order conversion</h3>
            <p className="text-sm text-slate-500">
              Show a "Create B2B Order" action on checkout so staff can convert current cart lines into a B2B sales
              order.
            </p>
            {toggleError && <p className="text-xs text-red-600 mt-2">{toggleError}</p>}
          </div>
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only"
              checked={Boolean(storeInfo?.checkoutB2BOrderEnabled)}
              onChange={(e) => handleCheckoutB2BOrderToggle(e.target.checked)}
              disabled={toggleSaving}
            />
            <span
              className={`w-12 h-6 flex items-center bg-gray-300 rounded-full p-1 transition ${
                storeInfo?.checkoutB2BOrderEnabled ? 'bg-primary' : ''
              } ${toggleSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`bg-white h-4 w-4 rounded-full shadow transform transition ${
                  storeInfo?.checkoutB2BOrderEnabled ? 'translate-x-6' : ''
                }`}
              />
            </span>
            <span className="ml-3 text-sm font-medium text-slate-700">
              {storeInfo?.checkoutB2BOrderEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Cashier must attach a customer before converting cart items to a B2B order.
        </p>
      </div>
      <div className="flex justify-between items-center mb-4"><h2 className="text-xl font-bold">Payment Methods</h2><button onClick={() => handleOpenModal()} className="flex items-center bg-primary text-white px-3 py-1 rounded-md text-sm"><FiPlus className="mr-1" /> Add Method</button></div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50"><tr><th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-left">Surcharge</th><th className="px-4 py-2 text-left">Enabled</th><th className="px-4 py-2 text-right">Actions</th></tr></thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {methods.map(method => (
            <tr key={method._id}>
              <td className="px-4 py-3">{method.name}</td>
              <td className="px-4 py-3">{method.surcharge}%</td>
              <td className="px-4 py-3"><span className={`px-2 inline-flex text-xs font-semibold rounded-full ${method.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{method.enabled ? 'Yes' : 'No'}</span></td>
              <td className="px-4 py-3 text-right space-x-2"><button onClick={() => handleOpenModal(method)} className="text-indigo-600"><FiEdit /></button><button onClick={() => handleDelete(method._id)} className="text-red-600"><FiTrash2 /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      {isModalOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-3 sm:p-4"><div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md max-h-[calc(100vh-1.5rem)] sm:max-h-[calc(100vh-2rem)] overflow-y-auto"><h2 className="text-lg font-bold mb-4">{editingMethod ? 'Edit' : 'Add'} Payment Method</h2><PaymentMethodForm method={editingMethod} onSave={handleSave} onCancel={handleCloseModal} /></div></div>}
    </div>
  );
};

export default PaymentSettings;
